// Feature-based LH-texture classifier — inverts the arranger's accompaniment generators via
// onset-synchronization × pitch-motion (the standard symbolic-MIR texture basis; see
// docs/architecture.md → "Bring-your-own-song (upload)"). Pure leaf — no
// engine/render/RNG. Precision-biased: emits a texture tag only when the dominant per-bar label
// is confident, else [] (the upload routes at the five-finger floor — a missing tag is safe, a
// wrong tag mis-routes).
import { CONFIG } from "../config";
import type { NoteEvent, SkillId } from "../types";

const EPS = 1e-3;
// Notes whose onsets fall within one chord window are one struck chord — the same
// grouping the engine/hand-split use, so a humanized (un-quantized) block chord reads
// as struck-together rather than splitting into a spurious sequential figure.
const CHORD_WINDOW_S = CONFIG.chordWindowMs / 1000;

/** Cluster note onsets into struck groups: a new group starts when a note's onset is more
 *  than a chord window past the current group's first onset. Returns the lowest MIDI per
 *  group in onset order (the figuration's pitch contour) plus per-group sizes. */
function clusterOnsets(lh: readonly NoteEvent[]): { sizes: number[]; lows: number[] } {
  const sorted = [...lh].sort((a, b) => a.onset - b.onset);
  const groups: { size: number; low: number; anchor: number }[] = [];
  for (const n of sorted) {
    const g = groups[groups.length - 1];
    if (g && n.onset - g.anchor <= CHORD_WINDOW_S) {
      g.size++;
      g.low = Math.min(g.low, n.midi);
    } else {
      groups.push({ size: 1, low: n.midi, anchor: n.onset });
    }
  }
  return { sizes: groups.map((g) => g.size), lows: groups.map((g) => g.low) };
}

interface BarFeat {
  onsets: number;
  verticality: number; // notes per distinct onset (≥~1.8 → tones struck together)
  struckChords: number; // onsets that strike ≥2 simultaneous tones
  span: number; // semitone range within the bar
  cell1535: boolean; // sequential low-high-mid-high (the Alberti cell)
  ascendThenDesc: boolean; // single notes rising to a peak then falling (arpeggio)
}

function barFeatures(lh: readonly NoteEvent[]): BarFeat | null {
  if (lh.length === 0) return null;
  const { sizes, lows } = clusterOnsets(lh);
  const verticality = lh.length / sizes.length;
  const struckChords = sizes.filter((s) => s >= 2).length;
  // Lowest note per struck group, in onset order → the figuration's pitch contour.
  const pitches = lows;
  const span = Math.max(...lh.map((n) => n.midi)) - Math.min(...lh.map((n) => n.midi));
  const [p0, p1, p2, p3] = pitches;
  const cell1535 =
    verticality < 1.5 &&
    p0 !== undefined &&
    p1 !== undefined &&
    p2 !== undefined &&
    p3 !== undefined &&
    p1 > p0 &&
    p2 < p1 &&
    p3 > p2;
  const peak = pitches.indexOf(Math.max(...pitches));
  const ascendThenDesc = verticality < 1.5 && peak > 0 && peak < pitches.length - 1;
  return { onsets: sizes.length, verticality, struckChords, span, cell1535, ascendThenDesc };
}

function labelBar(f: BarFeat): SkillId | null {
  if (f.verticality >= 1.8 || f.struckChords >= 2) return "blocked-chords"; // struck together
  if (f.onsets <= 1) return null; // a single sustained note — no texture to read
  if (f.cell1535) return "alberti-bass";
  if (f.span > 11 && f.ascendThenDesc) return "arpeggios"; // spans > an octave, up-then-down
  return "broken-chords"; // sequential chord tones within an octave
}

/**
 * The dominant LH accompaniment texture(s) of a piece (deduped), or [] when no label is
 * confident. `notes` + `bars` must share a time unit (seconds for real uploads).
 */
export function classifyTexture(notes: readonly NoteEvent[], bars: readonly number[]): SkillId[] {
  const lh = notes.filter((n) => n.hand === "left");
  const counts = new Map<SkillId, number>();
  let labelled = 0;
  for (let i = 0; i + 1 < bars.length; i++) {
    const lo = (bars[i] ?? 0) - EPS;
    const hi = (bars[i + 1] ?? 0) - EPS;
    const inBar = lh.filter((n) => n.onset >= lo && n.onset < hi);
    const f = barFeatures(inBar);
    const label = f ? labelBar(f) : null;
    if (label) {
      counts.set(label, (counts.get(label) ?? 0) + 1);
      labelled++;
    }
  }
  if (labelled === 0) return [];
  // Precision gate: the dominant label must cover ≥40% of the labelled bars.
  const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  return best && best[1] / labelled >= 0.4 ? [best[0]] : [];
}
